<template>
    <form-validation v-slot="{ bindings: validationBindings }">
        <flex-container :gap="Size.xl">
            <flex-container
                :gap="Size.md"
                no-wrap
                align-items="flex-start"
            >
                <div class="title-icon">
                    <svg-icon
                        :icon="BellIcon"
                        :size="IconSize.sm"
                        color="primary"
                        is-custom-icon
                    />
                </div>
                <flex-container
                    direction="column"
                    :size="9"
                    :gap="Size.xs"
                >
                    <text-heading sub color="gray-800">
                        Menselijke overname activeren
                    </text-heading>

                    <text-decorator color="gray-500">
                        Laat de AI Agent gesprekken automatisch overdragen aan een collega wanneer dat nodig is.
                    </text-decorator>
                </flex-container>
            </flex-container>

            <horizontal-line no-margin />

            <flex-container no-wrap>
                <flex-container :size="3" :gap="Size.sm">
                    <text-heading sub color="gray-800">
                        Gebruikers en meldingen
                    </text-heading>
                    <text-decorator color="gray-500">
                        Stel in wie meldingen ontvangt wanneer de AI Agent een menselijke overname start, en of er e-mails verstuurd moeten worden.
                    </text-decorator>
                </flex-container>
                <flex-container :size="7" :gap="Size.md">
                    <input-group v-model="notifiableUsers">
                        <template #input>
                            <user-multi-select-input show-chips />
                        </template>
                    </input-group>

                    <input-group v-model="sendEmailOnHandover">
                        <template #input="{ bindings }">
                            <label :for="bindings.ids.input">
                                <flex-container
                                    align-items="center"
                                    :gap="Size.sm"
                                    inline
                                    no-wrap
                                >
                                    <checkbox-input label="Verstuur ook een e-mail bij menselijke overname" />
                                    <text-decorator :size="FontSize.sm">
                                        Verstuur ook een e-mail bij menselijke overname
                                    </text-decorator>
                                </flex-container>
                            </label>
                        </template>
                    </input-group>

                    <input-group v-model="notifiableEmails" :schema="array().of(string().email())">
                        <template #label>
                            <input-label>
                                <text-decorator :size="FontSize.sm">
                                    Extra e-mailadressen (optioneel)
                                </text-decorator>
                            </input-label>
                        </template>
                        <template #input>
                            <email-multi-text-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <horizontal-line no-margin />

            <flex-container no-wrap>
                <flex-container :size="3" :gap="Size.sm">
                    <text-heading sub color="gray-800">
                        Beschikbaarheid & Timeout
                    </text-heading>
                    <text-decorator color="gray-500">
                        Bepaal op welke dagen en tijdstippen menselijke overnames mogelijk zijn, en stel in hoe lang de AI Agent wacht op een reactie.
                    </text-decorator>
                </flex-container>
                <flex-container :size="7" :gap="Size.xs">
                    <div class="assistent-hand-over-view__availability">
                        <template
                            v-for="(day, dayName) in availability"
                            :key="dayName"
                        >
                            <text-decorator :size="FontSize.sm" style="width:50%;">
                                {{ dayNames[dayName] }}
                            </text-decorator>
                            <input-group v-model="day.active">
                                <template #input>
                                    <switch-input variant="small" />
                                </template>
                            </input-group>
                            <span>
                                {{ day.active ? 'Aan' : 'Uit' }}
                            </span>
                            <input-group v-model="day.timeFrom">
                                <template #input>
                                    <select-input :items="times" />
                                </template>
                            </input-group>
                            <input-group v-model="day.timeTo">
                                <template #input>
                                    <select-input :items="times" />
                                </template>
                            </input-group>
                        </template>
                    </div>

                    <flex-container :padding-top="Size.xl">
                        <text-decorator :size="FontSize.sm">
                            Timeout menselijke overname
                        </text-decorator>
                        <flex-container no-wrap>
                            <flex-container :size="7">
                                <input-group v-model="delayInMinutes">
                                    <template #input>
                                        <range-input
                                            min="1"
                                            max="10"
                                            step="1"
                                        />
                                    </template>
                                </input-group>
                            </flex-container>
                            <flex-container :size="3">
                                <input-group
                                    v-model="delayInMinutes"
                                    :schema="number().min(1).max(10).required()"
                                    inline
                                >
                                    <template #input>
                                        <number-input
                                            :min="1"
                                            :max="10"
                                            edit-value
                                            hide-buttons
                                            show-native-buttons
                                        />
                                    </template>
                                </input-group>
                            </flex-container>
                        </flex-container>
                        <text-decorator :size="FontSize.sm">
                            Tijd voordat menselijke overname mislukt (minuten)
                        </text-decorator>
                    </flex-container>
                </flex-container>
            </flex-container>

            <horizontal-line no-margin />

            <flex-container no-wrap>
                <flex-container :size="3" :gap="Size.sm">
                    <text-heading sub color="gray-800">
                        Contacteer mij later (optioneel)
                    </text-heading>
                    <text-decorator color="gray-500">
                        Laat gebruikers hun gegevens achter als er tijdelijk geen medewerker beschikbaar is om het gesprek over te nemen.
                    </text-decorator>
                </flex-container>
                <flex-container :size="7">
                    <input-group v-model="contactMeLaterEnabled">
                        <template #input="{ bindings }">
                            <label :for="bindings.ids.input">
                                <flex-container
                                    align-items="center"
                                    :gap="Size.sm"
                                    inline
                                    no-wrap
                                >
                                    <checkbox-input label="Activeer ‘Contacteer mij later’-optie" />
                                    <text-decorator :size="FontSize.sm">
                                        Activeer ‘Contacteer mij later’-optie
                                    </text-decorator>
                                </flex-container>
                            </label>
                        </template>
                    </input-group>
                    <input-group v-model="fieldsToAskWhenContactingLater">
                        <template #label>
                            <input-label>
                                <text-decorator :size="FontSize.sm">
                                    Velden die opgevraagd moeten worden:
                                </text-decorator>
                            </input-label>
                        </template>
                        <template #input>
                            <checkbox-group-input
                                :items="fields"
                                :columns="2"
                            />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <horizontal-line no-margin />

            <card-normal background-color="gray-100">
                <flex-container :gap="Size.xs">
                    <flex-container :size="9">
                        <text-heading sub color="gray-800">
                            Menselijke overname activeren
                        </text-heading>
                    </flex-container>
                    <flex-container :size="1" justify-content="flex-end">
                        <input-group v-model="handoverEnabled" inline>
                            <template #input>
                                <switch-input variant="small" />
                            </template>
                        </input-group>
                    </flex-container>
                    <flex-container>
                        <text-decorator color="gray-500">
                            Maakt het mogelijk om gesprekken automatisch over te dragen aan een menselijke collega wanneer nodig.
                        </text-decorator>
                    </flex-container>
                </flex-container>
            </card-normal>

            <horizontal-line no-margin />

            <flex-container
                :padding-bottom="Size.xl"
                justify-content="flex-end"
            >
                <button-block
                    :disabled="!validationBindings.isValid"
                    @loading="save($event)"
                >
                    Opslaan
                </button-block>
            </flex-container>
        </flex-container>
    </form-validation>
</template>

<script setup lang="ts">
    import BellIcon from '@keyvue/one-awesome-icons/icons/solid/bell.svg?component';

    import { Assistent, AssistentId, ChangeHandoverSettingsCommand, User, UserId } from '@keyvue/one-api-client';
    import { computed, onMounted, ref } from 'vue';
    import { ButtonState, type ButtonStateChangedEvent, FontSize, IconSize, NotificationBlock, Size, useNotify } from '@keyvue/one-ui';
    import UserMultiSelectInput from '@/company/user/components/input/UserMultiSelectInput.vue';
    import { array, number, string } from 'yup';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import { onBeforeRouteUpdate } from 'vue-router';
    import EmailMultiTextInput from '@/shared/components/input/EmailMultiTextInput.vue';

    interface DayAvailability {
        timeFrom: { value: number, label: string },
        timeTo: { value: number, label: string },
        active: boolean
    }

    interface WeekAvailability {
        monday: DayAvailability,
        tuesday: DayAvailability,
        wednesday: DayAvailability,
        thursday: DayAvailability,
        friday: DayAvailability,
        saturday: DayAvailability,
        sunday: DayAvailability,
    }

    const assistent = ref<Assistent>();
    const notifiableUsers = ref<User[]>([]);
    const notifiableEmails = ref<string[]>([]);
    const delayInMinutes = ref<string>('2');
    const handoverEnabled = ref<boolean>(false);
    const sendEmailOnHandover = ref<boolean>(false);
    const contactMeLaterEnabled = ref<boolean>(false);
    const fieldsToAskWhenContactingLater = ref<{
        label: string,
        value: string,
    }[]>([]);

    const availability = ref<WeekAvailability>({
        monday: {
            timeFrom: { value: 900, label: '9:00' },
            timeTo: { value: 1700, label: '17:00' },
            active: false,
        },
        tuesday: {
            timeFrom: { value: 900, label: '9:00' },
            timeTo: { value: 1700, label: '17:00' },
            active: false,
        },
        wednesday: {
            timeFrom: { value: 900, label: '09:00' },
            timeTo: { value: 1700, label: '17:00' },
            active: false,
        },
        thursday: {
            timeFrom: { value: 900, label: '09:00' },
            timeTo: { value: 1700, label: '17:00' },
            active: false,
        },
        friday: {
            timeFrom: { value: 900, label: '09:00' },
            timeTo: { value: 1700, label: '17:00' },
            active: false,
        },
        saturday: {
            timeFrom: { value: 900, label: '09:00' },
            timeTo: { value: 1700, label: '17:00' },
            active: false,
        },
        sunday: {
            timeFrom: { value: 900, label: '09:00' },
            timeTo: { value: 1700, label: '17:00' },
            active: false,
        },
    });

    const { client } = useClient();
    const { notify } = useNotify();

    const props = defineProps<{
        modelValue: Assistent
        assistentId: string
    }>();

    onMounted(async () => {
        await loadHandoverSettings();
    });

    onBeforeRouteUpdate(async () => {
        await loadHandoverSettings();
    });

    async function loadHandoverSettings() {
        assistent.value = props.modelValue;

        const { handoverSettings } = assistent.value;
        const mappedFields = handoverSettings.fieldsToAskOnContactLater.map(
            (value) => mapField(value),
        );

        notifiableUsers.value = handoverSettings.notifiableUsers;
        notifiableEmails.value = handoverSettings.notifiableEmails;
        handoverEnabled.value = handoverSettings.handoverEnabled;
        sendEmailOnHandover.value = handoverSettings.sendEmailOnHandover;
        contactMeLaterEnabled.value = handoverSettings.contactMeLaterEnabled;
        delayInMinutes.value = handoverSettings.delayInMinutes.toString();
        fieldsToAskWhenContactingLater.value = mappedFields;

        availability.value.monday.active = handoverSettings.mondayActive;
        availability.value.monday.timeFrom = mapTime(handoverSettings.mondayTimeFrom);
        availability.value.monday.timeTo = mapTime(handoverSettings.mondayTimeTo);
        availability.value.tuesday.active = handoverSettings.tuesdayActive;
        availability.value.tuesday.timeFrom = mapTime(handoverSettings.tuesdayTimeFrom);
        availability.value.tuesday.timeTo = mapTime(handoverSettings.tuesdayTimeTo);
        availability.value.wednesday.active = handoverSettings.wednesdayActive;
        availability.value.wednesday.timeFrom = mapTime(handoverSettings.wednesdayTimeFrom);
        availability.value.wednesday.timeTo = mapTime(handoverSettings.wednesdayTimeTo);
        availability.value.thursday.active = handoverSettings.thursdayActive;
        availability.value.thursday.timeFrom = mapTime(handoverSettings.thursdayTimeFrom);
        availability.value.thursday.timeTo = mapTime(handoverSettings.thursdayTimeTo);
        availability.value.friday.active = handoverSettings.fridayActive;
        availability.value.friday.timeFrom = mapTime(handoverSettings.fridayTimeFrom);
        availability.value.friday.timeTo = mapTime(handoverSettings.fridayTimeTo);
        availability.value.saturday.active = handoverSettings.saturdayActive;
        availability.value.saturday.timeFrom = mapTime(handoverSettings.saturdayTimeFrom);
        availability.value.saturday.timeTo = mapTime(handoverSettings.saturdayTimeTo);
        availability.value.sunday.active = handoverSettings.sundayActive;
        availability.value.sunday.timeFrom = mapTime(handoverSettings.sundayTimeFrom);
        availability.value.sunday.timeTo = mapTime(handoverSettings.sundayTimeTo);
    }

    function mapField(field: string) {
        return fields.value.find(
            (value) => {
                return (value.value === field);
            },
        ) as { value: string, label: string };
    }

    function mapTime(time: number) {
        return times.value.find(
            (value) => (value.value === time),
        ) as { value: number, label: string };
    }

    async function save(event: ButtonStateChangedEvent) {
        const command = new ChangeHandoverSettingsCommand(
            new AssistentId(props.assistentId),
            notifiableUsers.value.map((user) => (user.id as UserId)),
            notifiableEmails.value,
            handoverEnabled.value,
            sendEmailOnHandover.value,
            parseInt(delayInMinutes.value, 10),
            contactMeLaterEnabled.value,
            fieldsToAskWhenContactingLater.value.map((field) => field.value),
            availability.value.monday.active,
            availability.value.monday.timeFrom.value,
            availability.value.monday.timeTo.value,
            availability.value.tuesday.active,
            availability.value.tuesday.timeFrom.value,
            availability.value.tuesday.timeTo.value,
            availability.value.wednesday.active,
            availability.value.wednesday.timeFrom.value,
            availability.value.wednesday.timeTo.value,
            availability.value.thursday.active,
            availability.value.thursday.timeFrom.value,
            availability.value.thursday.timeTo.value,
            availability.value.friday.active,
            availability.value.friday.timeFrom.value,
            availability.value.friday.timeTo.value,
            availability.value.saturday.active,
            availability.value.saturday.timeFrom.value,
            availability.value.saturday.timeTo.value,
            availability.value.sunday.active,
            availability.value.sunday.timeFrom.value,
            availability.value.sunday.timeTo.value,
        );

        try {
            await client.dispatch(command);
            event.button.setState(ButtonState.completed);
            notifySuccess();
        } catch (e) {
            notifyError();
        }
    }

    function notifySuccess() {
        notify({
            component: NotificationBlock,
            props: {
                title: 'Gelukt!',
                text: 'De instellingen zijn successvol opgeslagen',
                color: 'green-500',
            },
            duration: 2000,
        });
    }

    function notifyError() {
        notify({
            component: NotificationBlock,
            props: {
                title: 'Mislukt!',
                text: 'Er is iets misgegaan tijdens het opslaan',
                color: 'red-500',
            },
            duration: 2000,
        });
    }

    const dayNames = {
        monday: 'Maandag',
        tuesday: 'Dinsdag',
        wednesday: 'Woensdag',
        thursday: 'Donderdag',
        friday: 'Vrijdag',
        saturday: 'Zaterdag',
        sunday: 'Zondag',
    };

    /** Generates an array with value label objects for all periods in a day (00:00 to 23:45) */
    const times = computed(() => (
        Array.from(
            { length: 24 },
            (item, index) => index,
        ).flatMap((hour) => {
            const hh = hour.toString().padStart(2, '0');
            return ['00', '15', '30', '45'].map((minute) => ({
                label: `${hh}:${minute}`,
                value: parseInt(`${hh}${minute}`, 10),
            }));
        })
    ));

    const fields = computed(() => ([
        {
            label: 'Naam',
            value: 'name',
        }, {
            label: 'Adres',
            value: 'address',
        }, {
            label: 'Telefoonnummer',
            value: 'phone-number',
        }, {
            label: 'E-mail',
            value: 'e-mail',
        }, {
            label: 'Opmerkingen',
            value: 'comment',
        },
    ]));
</script>

<style lang="scss" scoped>
    .title-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: size(xs);
        height: 36px;
        width: 36px;
        box-sizing: border-box;
        border: 1px solid var(--theme-colors-default--primary)
    }

    .assistent-hand-over-view__availability{
        width: 100%;
        display: grid;
        grid-template-columns: min-content min-content 1fr 2fr 2fr;
        align-items: center;
        gap: size(xs) size(sm);
    }
</style>
